import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as z from 'zod';
import ImageUpload from '../components/common/ImageUpload';
import MintConfirmationModal from '../components/dialogs/mint-confirmation-dialog';
import TransactionSuccessModal from '../components/dialogs/transaction-success-dialog';
import Input from '../components/ui/Input';
import AuthContext from '../contexts/AuthContext';
import TokenListContext from '../contexts/TokenListContext';
import useAPI from '../hooks/useAPI';
import WalletContext from '../contexts/WalletContext';

const schema = z.object({
  tokenName: z.string().trim().min(1, 'Token name is required'),
  tokenSymbol: z
    .string()
    .min(1, 'Token symbol is required')
    .max(5, 'Symbol must be 5 characters or fewer')
    .regex(/^[a-zA-Z0-9]+$/, 'Only alphanumeric characters are allowed'),
  tokenLogo: z
    .string()
    .min(1, 'Logo is required')
    .refine(val => val !== null && val !== undefined && val !== '', {
      message: 'Logo is required',
    }),
  amount: z
    .string()
    .min(1, 'Total tokens must be greater than 0')
    .refine(val => Number(val) !== 0, {
      message: 'Enter a valid number of tokens',
    }),
  address: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

export default function MintTokenPage() {
  const [tokenLogo, setTokenLogo] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const {
    tokenList,
    totalToken,
    refetchTransactionData: refetchTokenList,
  } = useContext(TokenListContext);
  const { refetchTransactions } = useContext(WalletContext);
  const { refetchAdminBalance } = useContext(AuthContext);

  const navigate = useNavigate();
  const {
    refetch: mintToken,
    isLoading,
    data: mintTokenData,
  } = useAPI({
    url: '/token/mint/addcash',
    method: 'POST',
    onSuccess: () => {
      setOpen(false);
      setOpenSuccessModal(true);
      refetchAdminBalance();
      refetchTransactions();
      refetchTokenList();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const [tokenName, tokenSymbol, amount] = watch([
    'tokenName',
    'tokenSymbol',
    'amount',
  ]);

  const handleMintToken = (data: FormData) => {
    setOpen(true);
  };

  useEffect(() => {
    if (totalToken && totalToken > 0) {
      navigate('/token-management');
    }
  }, [tokenList]);

  const onConfirm = () => {
    mintToken({
      tokenName,
      tokenSymbol,
      amount,
      tokenLogo,
      tokenType: 'MINT',
    });
  };

  const { refetch: uploadLogo } = useAPI({
    url: '/upload/image',
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    isFormData: true,
    onSuccess: responseData => {
      if (responseData?.result) {
        setTokenLogo(responseData?.result);
        setValue('tokenLogo', responseData?.result);
        clearErrors('tokenLogo');
      }
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  const handleLogoUpload = (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    uploadLogo(formData);
  };

  const transactionId = useMemo(
    () => mintTokenData?.result?.data?.data?.transactionId,
    [mintTokenData],
  );

  return (
    <>
      <div className="mb-2 d-flex justify-content-between align-items-center">
        <div>
          <h2 className="fw-bold">Token Management</h2>
        </div>
      </div>

      <div className="bg-white p-4">
        <Row>
          <Col xs={12} md={6}>
            <p className="h4 mb-3 fw-bold">Token Details</p>
            <form onSubmit={handleSubmit(handleMintToken)}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="">
                  Token Name <span className="text-danger">*</span>
                </Form.Label>
                <Input
                  type="text"
                  {...register('tokenName')}
                  error={errors.tokenName?.message as string}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="">
                  Token Symbol <span className="text-danger">*</span>
                </Form.Label>
                <Input
                  type="text"
                  {...register('tokenSymbol')}
                  error={errors.tokenSymbol?.message as string}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <ImageUpload
                  error={errors.tokenLogo?.message as string}
                  name="tokenLogo"
                  placeholder="Upload logo"
                  onImageChange={handleLogoUpload}
                  accept={['image/png', 'image/jpeg', 'image/jpg']}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="">
                  Total Token to Mint <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }) => (
                    <div style={{ position: 'relative' }}>
                      <NumericFormat
                        {...field}
                        decimalScale={0}
                        allowNegative={false}
                        customInput={Input}
                        onValueChange={value => field.onChange(value.value)}
                        error={errors.amount?.message as string}
                        style={{
                          paddingRight: tokenList?.[0]?.tokenSymbol
                            ? '60px'
                            : '10px',
                        }}
                      />
                      {tokenList?.[0]?.tokenSymbol && (
                        <span
                          style={{
                            position: 'absolute',
                            right: '15px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#666',
                            fontWeight: 600,
                            pointerEvents: 'none',
                          }}
                        >
                          {tokenList[0].tokenSymbol}
                        </span>
                      )}
                    </div>
                  )}
                />
              </Form.Group>

              <div className="d-flex gap-2">
                {/* This will be in Next Phase */}
                {/* <Button
                  size="lg"
                  variant="outlined"
                  className="btn-dark bg-white text-dark">
                  Save as Draft
                </Button> */}
                <Button
                  size="lg"
                  variant="filled"
                  className="btn-dark"
                  type="submit"
                >
                  {'Mint Tokens'}
                </Button>
              </div>
            </form>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ maxWidth: 357 }} className="mx-auto px-md-5">
              <img className="mx-auto" src="/assets/images/mint_token.svg" />
              <p className="mb-1">
                Streamline your token creation process with our intuitive Token
                Management module. This module empowers you to manage and mint
                tokens seamlessly, ensuring a smooth deployment.
              </p>
            </div>
          </Col>
        </Row>
        <MintConfirmationModal
          open={open}
          isLoading={isLoading}
          onReject={() => setOpen(false)}
          onConfirm={onConfirm}
          information={{
            tokenNameOrSymbol: `${tokenName} (${tokenSymbol})`,
            tokensToMint: Number(amount),
          }}
        />
        <TransactionSuccessModal
          open={openSuccessModal}
          title="Tokens minted successfully"
          isCloseIconVisible={true}
          onClose={() => {
            setOpenSuccessModal(false);
            navigate('/token-management');
          }}
        />
      </div>
    </>
  );
}
